import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, ChevronDown, ChevronRight, Search, 
  Users, Tag, Calendar, RefreshCw, Filter, X, TrendingDown, FilterX, Download
} from 'lucide-react';
import { 
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend, LabelList
} from 'recharts';
import './DashboardBultos.css';
import DashboardMenuHeader from '../components/DashboardMenuHeader';
import { useQuery } from '../QueryContext'; // Contexto correcto
import { useAuth } from '../AuthContext';
import { clearCache } from '../utils/dashboardCache';
import FullScreenLoader from './FullScreenLoader';
import GlobalSearch from '../components/GlobalSearch';
import { calculateWorkingDays } from '../utils/timeUtils';
import * as XLSX from 'xlsx';

/* eslint-disable react-hooks/exhaustive-deps, no-unused-vars */

const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const filterLabels = {
  BI_PROVEEDOR: "Casas",
  BI_MARCA: "Marca",
  BI_LINEA: "Línea",
  CLI_SEG_DES: "Segmento",
  CLI_PRIN: "Cliente",
  CLI_VEN_DES: "Vendedor",
  CLI_CANAL: "Canal",
  CLI_REG_VERD: "Región Filtro",
  V_ANULADA: "Factura Anulada",
  CLI_INACTIVO: "Cliente Inactivo",
  BI_CLASIF2: "Clasificación",
  BI_DESC1: "Producto",
  CLI_TERRITORIO: "Territorio",
  CLI_CANAL_REAL: "Canal 2",
  BI_SEGMENTO: "Portafolio",
  CLI_NOMBRE: "Cliente Detalle",
  V_FACTURA: "Factura",
  CLIENTE_ID: "Cliente ID",
  ART_ID: "SKU ID",
  BI_ASP: "ASP"
};

const formatNumber = (num) => new Intl.NumberFormat('de-DE').format(Math.round(num || 0));
const formatK = (num) => (num >= 1000 || num <= -1000) ? `${(num / 1000).toFixed(1)}K` : Math.round(num || 0);
const formatDollar = (num) => `$${formatNumber(num)}`;

const formatRegionName = (name) => {
  if (!name) return 'N/A';
  return name.split('(')[0].trim();
};

const getSemaforoColor = (p) => {
  const val = Number(p || 0);
  if (val < 80) return '#8b0000'; // Rojo Oscuro
  if (val < 90) return '#ff4d4d'; // Rojo Claro
  if (val < 100) return '#ffcc00'; // Amarillo
  return '#00ba52'; // Verde
};

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
  // Ocultar etiquetas de segmentos muy pequeños para evitar solapamiento (opcional, recomendado 3%)
  if (percent < 0.03) return null;

  const RADIAN = Math.PI / 180;
  // Aumentamos ligeramente el radio para que la etiqueta no toque el borde del pastel
  const radius = outerRadius + 12; 
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text 
      x={x} 
      y={y} 
      fill="#cbd5e1" // Un gris azulado suave más elegante que el blanco puro
      fontSize="10" 
      fontWeight="600"
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(1)}%`}
    </text>
  );
};

const MultiSelectFilter = ({ label, name, icon: Icon, options = [], selectedValues, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ✅ LÓGICA MEJORADA: "Todos" cuando todos están seleccionados
  const totalOptions = options.length;
  const hasSelection = selectedValues?.length > 0;
  const allOptionsSelected = totalOptions > 0 && selectedValues.length === totalOptions;
  
  const displayValue = allOptionsSelected 
    ? 'Todos'                          // ✅ ¡MÁGICO!
    : hasSelection 
      ? (selectedValues.length === 1 
          ? options.find(opt => (opt.value || opt) === selectedValues[0])?.label || selectedValues[0]
          : `${selectedValues.length} Selec.`) 
      : 'Todos';                        // ✅ Vacío = Todos
  
  const showSearch = options.length > 12;
  const filteredOptions = options.filter(opt => 
    String(opt.label || opt).toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 100);
  
  const areAllSelected = filteredOptions.every(opt => 
    selectedValues.includes(opt.value || opt)
  );

  const handleSelectAll = () => {
    console.log(`🔄 ${name}: SelectAll - filtered: ${filteredOptions.length}, total: ${totalOptions}, current: ${selectedValues.length}`);
    
    if (areAllSelected) {
      const newSelected = selectedValues.filter(val => 
        !filteredOptions.some(opt => (opt.value || opt) === val)
      );
      onChange(name, newSelected);
    } else {
      const newValuesOnly = filteredOptions.map(opt => opt.value || opt);
      onChange(name, newValuesOnly);
    }
  };

 const handleSingleToggle = useCallback((optValue) => { // ← useCallback
  const newVals = selectedValues.includes(optValue)
    ? selectedValues.filter(v => v !== optValue)
    : [...selectedValues, optValue];
  onChange(name, newVals);
}, [selectedValues, onChange, name]);

  return (
    <div className="filter-pill-container" ref={dropdownRef}>
      <button 
        className={`filter-pill-button ${hasSelection ? 'active' : ''}`} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon size={12} className="pill-icon" />
        <span className="pill-label">{label}:</span>
        <span className="pill-value">{displayValue}</span>
        <ChevronDown size={11} className={`pill-arrow ${isOpen ? 'up' : ''}`} />
      </button>
      {isOpen && (
        <div className={`filter-dropdown-panel ${showSearch ? 'with-search' : ''}`}>
          {showSearch && (
            <div className="search-wrapper">
              <Search size={14} className="search-icon" />
              <input 
                type="text" 
                placeholder="Buscar..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                autoFocus 
              />
            </div>
          )}
          {filteredOptions.length > 0 && (
            <label className="checkbox-row select-all-row">
              <input 
                type="checkbox" 
                checked={areAllSelected} 
                onChange={handleSelectAll} 
              />
              <span>Seleccionar Todos ({filteredOptions.length})</span>
            </label>
          )}
          <div className="options-list">
            {filteredOptions.map((opt, i) => {
              const optValue = opt.value || opt;
              const optLabel = opt.label || opt;
              
              return (
                <label key={`${name}-${optValue}-${i}`} className="checkbox-row">
                  <input 
                    type="checkbox" 
                    checked={selectedValues.includes(optValue)} 
                    onChange={() => handleSingleToggle(optValue)}
                  />
                  <span>{optLabel}</span>
                </label>
              );
            })}
          </div>
          {hasSelection && (
            <button className="reset-pill-link" onClick={() => {
              console.log(`🗑️ ${name}: Limpiar`);
              onChange(name, []);
            }}>
              Limpiar ({selectedValues.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
};

const nombresMeses = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

const INITIAL_FILTERS = {
  BI_PROVEEDOR: [], BI_MARCA: [], BI_LINEA: [], CLI_SEG_DES: [],
  CLI_PRIN: [], CLI_VEN_DES: [], CLI_CANAL: [], CLI_REG_VERD: [], 
  V_ANULADA: [], CLI_INACTIVO: [], BI_CLASIF2: [], BI_DESC1: [],
  CLI_TERRITORIO: [], CLI_CANAL_REAL: [], BI_SEGMENTO: [], CLI_NOMBRE : [],
  V_FACTURA: [], CLIENTE_ID: [], ART_ID: [],  BI_ASP: []
};

const DashboardUSDSales = () => {
  // Si ya tenemos caché, no mostramos loading inicial
  
  const { salesQueryBultosUSD, getSalesFilters, loading: apiLoading } = useQuery(); 
  const { profile } = useAuth();

  // Define estos estados que te faltaban:
  const [localLoading, setLocalLoading] = useState(true);
  const [querying, setQuerying] = useState(false);
  const [metrics, setMetrics] = useState({ 
    v_cantidad: 0, q_cuota: 0, porcentaje: 0,
    var_v_anual: 0, var_v_mensual: 0,
    var_q_anual: 0, var_q_mensual: 0,
    var_p_anual: 0, var_p_mensual: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [asesoresTableData, setAsesoresTableData] = useState([]);
  const [marcasTableData, setMarcasTableData] = useState([]);
  const [proveedoresTableData, setProveedoresTableData] = useState([]);
  const [tableTotals, setTableTotals] = useState({ venta: 0, cuota: 0, efectividad: 0 });
  const [selectedMeses, setSelectedMeses] = useState([nombresMeses[new Date().getMonth()]]);
  const [selectedDay, setSelectedDay] = useState([]); // ✅ NUEVO ESTADO PARA EL DÍA
  // Changed default year from new Date().getFullYear() to [2024, 2025, 2026] to show all available data
  const currentYear = new Date().getFullYear();
  const [selectedAnios, setSelectedAnios] = useState([currentYear]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [availableOptions, setAvailableOptions] = useState({});
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [salesConfig, setSalesConfig] = useState(null);
  const [comparisonMode, setComparisonMode] = useState('CUOTA'); // 'CUOTA' or 'FORECAST'
  const [devMode, setDevMode] = useState('NONE'); // NONE, DEV, FIN, NETO
  const [expandedMarcas, setExpandedMarcas] = useState({});
  const requestRef = useRef(0);
  const filtersInitialized = useRef(false);
  const [workDayMetrics, setWorkDayMetrics] = useState({ total: 0, elapsed: 0, percentage: 0 });
  const [lowCompliance, setLowCompliance] = useState(false);

  const toggleMarca = (marca) => {
    setExpandedMarcas(prev => ({ ...prev, [marca]: !prev[marca] }));
  };

  const COLORS = ['#1a2c4e', '#0068b3', '#00ba52', '#4f8cff', '#00e6b8', '#334767'];
  

  useEffect(() => {
    const metrics = calculateWorkingDays(selectedMeses, selectedAnios, nombresMeses);
    setWorkDayMetrics(metrics);
  }, [selectedMeses, selectedAnios]);

  const [availableYears, setAvailableYears] = useState([]);

// En loadDashboardData:
useEffect(() => {
  const loadDashboardData = async () => {
    setLocalLoading(true);
    try {
      const config = await getSalesFilters();
      // ✅ Manejar el caso donde config es null o undefined
      if (!config) {
        throw new Error("Configuración de ventas no disponible.");
      }
      setSalesConfig(config);
      
      setAvailableOptions(config.availableOptions || {});
      setAvailableYears(config.availableYears || [currentYear]);
      
      // 🎯 PREFILTRAR CLI_SEG_DES LOCALMENTE
      const allSegments = config.availableOptions?.CLI_SEG_DES || [];
      const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
      
      // ✅ SELECCIONAR TODOS MENOS los excluidos
      const prefilteredSegments = allSegments.filter(segment => 
        !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
      );
      
      const prefilteredFilters = {
        ...INITIAL_FILTERS,
        V_ANULADA: ['0'],
        CLI_INACTIVO: ['0'],
        CLI_SEG_DES: prefilteredSegments  // 🎯 ← ESTO ES LA MAGIA
      };
      
      setFilters(prefilteredFilters);
      
      console.log('🎯 SEGMENTO PREFILTRADO LOCAL:', {
        todos: allSegments.length,
        seleccionados: prefilteredSegments.length,
        sample: prefilteredSegments.slice(0, 3),
        excluidos: allSegments.filter(s => excludedSegments.some(ex => s.toUpperCase().includes(ex.toUpperCase())))
      });
      
      // Años y meses
      const defaultYear = config.currentYear || currentYear;
      setSelectedAnios([defaultYear]);
      setSelectedMeses([nombresMeses[new Date().getMonth()]]);
      
      filtersInitialized.current = true;
      
    } catch (err) {
      console.error('Error al cargar la configuración del dashboard:', err);
    } finally {
      setLocalLoading(false);
    }
  };
  loadDashboardData();
}, []); // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleRefresh = async () => {
    clearCache();
    // No necesitamos setLocalLoading(true) aquí porque fetchData maneja setQuerying
    await fetchData();
};

const handleResetFilters = useCallback(() => {
  const config = salesConfig;
  if (!config?.availableOptions?.CLI_SEG_DES) return;
  
  // 🎯 MISMO PREFILTRADO
  const allSegments = config.availableOptions.CLI_SEG_DES;
  const excludedSegments = ['DONACIONES', 'EMPLEADOS', 'NO APLICA'];
  const prefilteredSegments = allSegments.filter(segment => 
    !excludedSegments.some(ex => segment.toUpperCase().includes(ex.toUpperCase()))
  );
  
  const resetFilters = {
    ...INITIAL_FILTERS,
    V_ANULADA: ['0'],
    CLI_INACTIVO: ['0'],
    CLI_SEG_DES: prefilteredSegments
  };
  
  setFilters(resetFilters);
  console.log('🔄 Reset Segmento prefiltrado');
}, [salesConfig]);

const processResponse = (response) => {
  console.log('🔍 RAW RESPONSE:', response);
  
  // ✅ KPIS - OK
   const kpis = Array.isArray(response.kpis) ? response.kpis[0] || {} : (response.kpis || {});
  const v_actual = Number(kpis.v_actual || 0);
  const q_actual = Number(kpis.q_actual || 0);
  const v_anio_ant = Number(kpis.v_anio_ant || 0); // Asegúrate de que este valor exista en tu backend
  const v_mes_ant = Number(kpis.v_mes_ant || 0);
  const q_anio_ant = Number(kpis.q_anio_ant || 0);
  const q_mes_ant = Number(kpis.q_mes_ant || 0);
  const p_anio_ant = Number(kpis.p_anio_ant || 0);
  const p_mes_ant = Number(kpis.p_mes_ant || 0);

  const porcentaje = q_actual > 0 ? Math.round((v_actual / q_actual) * 100 * 10) / 10 : 0;
  const var_v_anual = v_anio_ant !== 0 ? Math.round(((v_actual - v_anio_ant) / Math.abs(v_anio_ant)) * 100 * 10) / 10 : 0;
  const var_v_mensual = v_mes_ant !== 0 ? Math.round(((v_actual - v_mes_ant) / Math.abs(v_mes_ant)) * 100 * 10) / 10 : 0;
  const var_q_anual = q_anio_ant !== 0 ? Math.round(((q_actual - q_anio_ant) / Math.abs(q_anio_ant)) * 100 * 10) / 10 : 0;
  const var_q_mensual = q_mes_ant !== 0 ? Math.round(((q_actual - q_mes_ant) / Math.abs(q_mes_ant)) * 100 * 10) / 10 : 0;
  const var_p_anual = p_anio_ant !== 0 ? Math.round(((porcentaje - p_anio_ant) / Math.abs(p_anio_ant)) * 100 * 10) / 10 : 0;
  const var_p_mensual = p_mes_ant !== 0 ? Math.round(((porcentaje - p_mes_ant) / Math.abs(p_mes_ant)) * 100 * 10) / 10 : 0;

  const metrics = {
    v_cantidad: v_actual,
    q_cuota: q_actual,
    porcentaje,
    var_v_anual,
    var_v_mensual,
    var_q_anual,
    var_q_mensual,
    var_p_anual,
    var_p_mensual,
  };

  // 🎯 FIX: USA SOLO 'trend' para el gráfico de tendencia
  console.log('📈 TREND RAW:', response.trend);

const chartData = (response.trend || [])
  .filter(item => {
    const anioItem = Number(item.anio_num);
    return selectedAnios.includes(anioItem);
  })
  .map(item => {
    const cuota = Number(item.Cuota || 0);
    const venta = Number(item.Venta || 0);
    const ventaPY = Number(item.Venta_PY || 0);
    const cuotaPY = Number(item.Cuota_PY || 0);
    
    // ✅ GARANTIZAR NOMBRE ÚNICO SIEMPRE
    const anioNum = Number(item.anio_num) || new Date().getFullYear();
    const mesNum = Number(item.mes_num) || 1;
    
    // Construir nombre único: "ENE-2024", "FEB-2024", etc.
    const mesAbrev = nombresMeses[mesNum - 1]?.substring(0, 3).toUpperCase() || 'MES';
    const nombreUnico = `${mesAbrev}-${anioNum}`;
    
    // Verificar si el nombre del backend ya incluye el año
    const backendName = item.name || '';
    const tieneAnio = /\d{4}/.test(backendName);
    
    return {
      name: tieneAnio ? backendName : nombreUnico, // Usar backend si tiene año, sino generar
      Cuota: cuota,
      Venta: venta,
      Venta_PY: ventaPY,
      Cuota_PY: cuotaPY,
      Logro: cuota > 0 ? Math.round((venta / cuota) * 100 * 10) / 10 : 0,
      _anio: anioNum,
      _mes: mesNum,
      // ✅ Datos para debugging
      _backendName: backendName,
      _nombreGenerado: nombreUnico
    };
  })
  .filter(item => item.Cuota > 0 || item.Venta > 0)
  .sort((a, b) => {
    if (a._anio !== b._anio) return a._anio - b._anio;
    return a._mes - b._mes;
  });

console.log('✅ CHARTDATA FINAL:', chartData);

  // ✅ PIE MARCAS - desde regional
  const marcaMap = {};
  (response.regional || []).forEach(r => {
    const marca = r.BI_MARCA || r.marca || 'Sin Marca';
    marcaMap[marca] = (marcaMap[marca] || 0) + Number(r.venta || r.VENTA || 0);
  });
  const pieData = (response.marca_pie || [])
    .filter(item => item.marca && item.marca !== 'SIN_MARCA')
    .map(item => ({
      name: item.marca,
      value: Number(item.venta || 0)
    }))
    .sort((a,b) => b.value - a.value)
    .slice(0, 8);

  // ✅ REGION BAR
  const regionMap = {};
  (response.regional || []).forEach(r => {
    const reg = formatRegionName(r.CLI_REG_VERD || r.region || 'N/A');
    regionMap[reg] = (regionMap[reg] || 0) + Number(r.venta || r.VENTA || 0);
  });
  const regionData = Object.entries(regionMap)
    .map(([name, value]) => ({ name, value: Number(value) }))
    .sort((a,b) => b.value - a.value)
    .slice(0, 10);

  // ✅ TABLES

    const clients = (response.clientes_table || [])
  .filter(row => row.CLI_PRIN && row.CLI_PRIN !== 'TOTAL GENERAL')
  .map(r => ({
    CLI_PRIN: r.CLI_PRIN || 'N/A',
    venta: Number(r.venta || 0),
    cuota: Number(r.cuota || 0),
    efectividad: Number(r.efectividad || 0)
  })); // ✅ Orden por ranking del backend


  // ✅ CALCULAR TOTALES DE TABLA
  const tableTotals = {
    venta: clients.reduce((acc, c) => acc + c.venta, 0),
    cuota: clients.reduce((acc, c) => acc + c.cuota, 0),
  };
  tableTotals.efectividad = tableTotals.cuota > 0 ? (tableTotals.venta / tableTotals.cuota) * 100 : 0;



  // ✅ ASESores
  console.log('🔍 asesores_table RAW:', response.asesores_table);
console.log('🔍 regional RAW:', response.regional);

console.log('🔍 Usando REGIONAL (41) como asesores...');

const asesores = (response.regional || [])
  .filter(r => {
    // Filtra asesores válidos del regional (región+asesor)
    const asesor = r.asesor || r.CLI_VEN_DES;
    const hasValidName = asesor && asesor !== 'SIN_VEN' && asesor.trim() !== '';
    return hasValidName;
  })
  .reduce((acc, r) => {
    // Agrupa por asesor (elimina duplicados por región)
    const asesor = r.asesor || r.CLI_VEN_DES;
    const existing = acc.find(a => a.CLI_VEN_DES === asesor);
    
    if (existing) {
      existing.venta += Number(r.venta || 0);
      existing.cuota += Number(r.cuota || 0);
      existing.efectividad = existing.cuota > 0 ? 
        Math.round((existing.venta / existing.cuota) * 100 * 10) / 10 : 0;
    } else {
      acc.push({
        CLI_VEN_DES: asesor,
        venta: Number(r.venta || 0),
        cuota: Number(r.cuota || 0),
        efectividad: Number(r.efectividad || 0)
      });
    }
    return acc;
  }, [])
  .sort((a, b) => b.venta - a.venta)
  .slice(0, 20);

// 🔥 HOTFIX MARCAS v2: Desde marca_pie (6 marcas) + SKUs genéricos
console.log('🔍 marca_pie:', response.marca_pie?.length);
console.log('🔍 sku_detail sample:', response.sku_detail?.slice(0,2));

// 1. Marcas principales desde marca_pie
// ⭐ Backend marcas → Objetos con SKUs
// ⭐ 1 Query → Objetos completos
const marcasTableData = (response.marcas_table || []).map(row => {
    const skusRaw = (row.skus_data || '').split('###').filter(Boolean);
    const skus = skusRaw.map(skuStr => {
      const [biDesc1, venta, cuota, efectividad] = skuStr.split('|');
      return {
        BI_DESC1: biDesc1 || 'N/A',
        venta: parseFloat(venta) || 0,
        cuota: parseFloat(cuota) || 0,
        efectividad: parseFloat(efectividad) || 0
      };
    });
  
  return {
    BI_MARCA: row.BI_MARCA,
    venta: parseFloat(row.venta) || 0,
    cuota: parseFloat(row.cuota) || 0,
    efectividad: parseFloat(row.efectividad) || 0,
    skus: skus
  };
}).filter(m => m.BI_MARCA !== 'SIN_MARCA');

console.log('✅ 1-QUERY MARCAS+SKUs:', marcasTableData.length);
console.log('KOTEX:', marcasTableData.find(m => m.BI_MARCA === 'KOTEX')?.skus.slice(0,2));

const proveedoresTableData = (response.proveedores_table || [])
    .filter(row => row.BI_PROVEEDOR && row.BI_PROVEEDOR !== 'TOTAL GENERAL')
    .map(r => ({
      BI_PROVEEDOR: r.BI_PROVEEDOR || 'N/A',
      venta: Number(r.venta || 0),
      cuota: Number(r.cuota || 0),
      efectividad: Number(r.efectividad || 0)
    }));

  console.log('✅ DATA PROCESSED:', {
    chartData: chartData.length,
    pieData: pieData.length,
    regionData: regionData.length,
    clients: clients.length,
    asesores: asesores.length,
    marcas: marcasTableData.length,
    proveedores: proveedoresTableData.length,
  });

  return {
  metrics,
  chartData,
  pieData,
  regionData,
  tableData: clients,
  asesoresTableData: asesores,
  marcasTableData,
  proveedoresTableData,
  tableTotals,
};
};

 const exportToExcel = (data, filename, type) => {
    let rows = [];
    
    if (type === 'generic') {
      rows = data.map(item => ({
        Nombre: item.CLI_PRIN || item.CLI_VEN_DES || item.BI_MARCA || item.BI_PROVEEDOR,
        Cuota: Math.round(item.cuota || 0),
        Venta_USD: Math.round(item.venta || 0), 
        Efectividad_Pct: Math.round(item.efectividad || 0)
      }));
    } else if (type === 'hierarchical') {
      data.forEach(m => {
        m.skus.forEach(s => {
          rows.push({
            Marca: m.BI_MARCA, SKU: s.BI_DESC1, 
            Cuota: Math.round(s.cuota || 0), Venta_USD: Math.round(s.venta || 0),
            Efectividad_Pct: Math.round(s.efectividad || 0)
          });
        });
      });
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${filename}_${new Date().getTime()}.xlsx`);
  };

 const fetchData = useCallback(async () => {
  if (!filtersInitialized.current) return;
  
  const requestId = ++requestRef.current;
  setQuerying(true);
  try {
    const safeAnios = Array.isArray(selectedAnios) ? selectedAnios : [];
    const safeMeses = Array.isArray(selectedMeses) ? selectedMeses : []; // Asegúrate de que selectedMeses sea un array

    const payload = {
      metric_type: 'USD',
      comparison_mode: comparisonMode, // Usar el estado
      dev_mode: devMode, // ✅ ENVIAR AL BACKEND
      page_anios: safeAnios.filter(a => a),  // ✅ Filtra falsy
      page_meses: safeMeses.filter(m => m),
      page_dias: selectedDay.filter(d => d), // ✅ AÑADIR DÍAS AL PAYLOAD
      filters: filters,
      breakdown_type: 'ASESOR',
      chart_year: safeAnios?.[0] || null,
      low_compliance: lowCompliance,
      user_info: profile ? {
          username: profile.username,
          cargo: profile.cargo
        } : null
    };

    console.log('📤 Payload con USER_INFO:', {
        ...payload,
        user_info: payload.user_info ? `${payload.user_info.username} (${payload.user_info.cargo})` : 'Sin usuario'
      });

    // 🎯 DEBUG MEJORADO
    console.log('📤 Payload completo:', {
      ...payload,
      filters_summary: {
        total_filters: Object.keys(filters).length,
        active_filters: Object.values(filters).filter(arr => arr.length > 0).length,
        v_anulada: filters.V_ANULADA,
        cli_inactivo: filters.CLI_INACTIVO,
        dynamic_count: Object.entries(filters).filter(([k]) => !['V_ANULADA', 'CLI_INACTIVO'].includes(k)).reduce((sum, [k, v]) => sum + v.length, 0)
      }
    });

    const rawResponse = await salesQueryBultosUSD(payload);
    console.log('📥 Backend response:', rawResponse);
    
    if (requestId !== requestRef.current) return; // Ignorar si hay una petición más nueva

    const processed = processResponse(rawResponse);
    setMetrics(processed.metrics);
    setChartData(processed.chartData);
    setPieData(processed.pieData);
    setRegionData(processed.regionData);
    setTableData(processed.tableData);
    setAsesoresTableData(processed.asesoresTableData);
    setTableTotals(processed.tableTotals || {});  // ✅ ⭐ SOLO ESTA LÍNEA NUEVA
    setMarcasTableData(processed.marcasTableData);
    setProveedoresTableData(processed.proveedoresTableData);
    
  } catch (err) {
    if (requestId === requestRef.current) {
      console.error("❌ Error fetchData:", err);
    }
  } finally {
    if (requestId === requestRef.current) {
      setQuerying(false);
    }
  }
}, [comparisonMode, devMode, selectedMeses, selectedAnios, selectedDay, filters, lowCompliance, salesQueryBultosUSD] /* eslint-disable-next-line react-hooks/exhaustive-deps */ );

// 🔍 DEBUG: Muestra qué filtros tienes disponibles
useEffect(() => {
  console.log('🔍 availableOptions completo:', availableOptions);
  
  // ← FORZAR todos los filtros del filterLabels si no llegan del backend
  if (Object.keys(availableOptions).length === 0 && filtersInitialized.current) {
    const allFilterKeys = Object.keys(filterLabels);
    const fullOptions = {};
    
    allFilterKeys.forEach(key => {
      fullOptions[key] = ['Opción 1', 'Opción 2', 'Opción 3']; // ← Placeholder
    });
    
    setAvailableOptions(fullOptions);
  }
}, [availableOptions, filtersInitialized.current]);

// Debounced auto-fetch - Solo ejecuta si no está querying
  // ✅ NUEVO useEffect con debounce correcto:
const debouncedFetch = useRef();

useEffect(() => {
  if (!filtersInitialized.current) return;
  
  if (debouncedFetch.current) {
    clearTimeout(debouncedFetch.current);
  }
  
  debouncedFetch.current = setTimeout(() => {
    fetchData();
  }, 300);
  
  return () => {
    if (debouncedFetch.current) {
      clearTimeout(debouncedFetch.current);
    }
  };
}, [comparisonMode, devMode, selectedMeses, selectedAnios, selectedDay, filters, lowCompliance]);

  // Variantes para la animación de entrada/salida
  const pageVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  // Tooltip personalizado para el gráfico de barras
  // Tooltip personalizado para el gráfico de barras
const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    // ✅ OBTENER DATOS COMPLETOS DEL ITEM
    const itemData = payload[0].payload;
    
    // ✅ Debug: Verificar qué nombre estamos mostrando
    console.log('🎯 Tooltip USD itemData:', {
      name: itemData.name,
      backendName: itemData._backendName,
      nombreGenerado: itemData._nombreGenerado,
      anio: itemData._anio,
      mes: itemData._mes,
      Cuota: itemData.Cuota,
      Venta: itemData.Venta,
      Logro: itemData.Logro
    });
    
    return (
      <div className="custom-tooltip" style={{ 
        backgroundColor: 'white', 
        padding: '12px', 
        borderRadius: '8px', 
        boxShadow: '0 4px 12px rgba(0,0,0,0.25)', 
        border: '1px solid #e0e0e0',
        minWidth: '180px'
      }}>
        {/* ✅ NOMBRE ÚNICO GARANTIZADO */}
        <p style={{ 
          color: '#333', 
          fontWeight: 'bold', 
          marginBottom: '8px', 
          fontSize: '0.9rem',
          borderBottom: '1px solid #eee',
          paddingBottom: '5px'
        }}>
          {itemData.name}
        </p>
        
        {/* ✅ Valores principales con formato USD */}
        <p style={{ color: '#1a2c4e', fontSize: '0.85rem', margin: '4px 0' }}>
          <strong>{comparisonMode === 'FORECAST' ? 'Forecast' : 'Cuota'}:</strong> {formatDollar(itemData.Cuota || 0)}
        </p>
        <p style={{ color: '#0068b3', fontSize: '0.85rem', margin: '4px 0' }}>
          <strong>USD Real:</strong> {formatDollar(itemData.Venta || 0)}
        </p>
        <p style={{ 
          color: '#00ba52', 
          fontSize: '0.85rem', 
          margin: '4px 0', 
          fontWeight: 'bold',
          backgroundColor: 'rgba(0, 186, 82, 0.1)',
          padding: '2px 6px',
          borderRadius: '3px',
          display: 'inline-block'
        }}>
          Logro: {itemData.Logro || 0}%
        </p>
        
        {/* ✅ Comparativo año anterior (opcional) */}
        {(itemData.Venta_PY > 0 || itemData.Cuota_PY > 0) && (
          <div style={{ 
            borderTop: '1px solid #eee', 
            marginTop: '8px', 
            paddingTop: '5px',
            fontSize: '0.75rem',
            color: '#666'
          }}>
            <p style={{ margin: '2px 0' }}>
              <strong>Año Ant. Venta:</strong> {formatDollar(itemData.Venta_PY)}
            </p>
            <p style={{ margin: '2px 0' }}>
              <strong>Año Ant. Cuota:</strong> {formatDollar(itemData.Cuota_PY)}
            </p>
          </div>
        )}
      </div>
    );
  }
  return null;
};

  const filterTitle = ` - ${selectedMeses.join(', ')} - ${selectedAnios.join(', ')}${selectedDay.length > 0 ? ` - Día ${selectedDay.join(', ')}` : ''}`; // ✅ Mostrar día

  return (
    <motion.div 
      className="dashboard-container"
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
      transition={isMobile ? { duration: 0.3 } : { duration: 0.6, ease: "easeInOut" }}
    >
      <DashboardMenuHeader />

{(apiLoading || localLoading) && !filtersInitialized.current ? (
  <FullScreenLoader message="Cargando datos del servidor..." /> 
) : null}
{querying && (
  <div className="querying-overlay active" />
)}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={isMobile ? { duration: 0.2 } : { duration: 0.5, ease: "easeOut" }}
      >
      <header className="header-flex">
        <div className="title-section">
          <h1 className="dashboard-title">Valor USD</h1>
          <div className="title-underline"></div>
        </div>
        <div className="date-selectors">
          <button 
            className="refresh-button" 
            onClick={handleRefresh}
            title="Actualizar Datos"
            disabled={querying}
          >
            <RefreshCw size={16} className={querying ? "spin-anim" : ""} />
          </button>
          <button 
            className="mobile-filter-btn" 
            onClick={() => setShowMobileFilters(true)}
          >
            <Filter size={16} />
            <span>Filtros</span>
          </button>
          <div className="switch-toggle-group">
            <button className={`switch-toggle-btn ${comparisonMode === 'CUOTA' ? 'active' : ''}`} onClick={() => setComparisonMode('CUOTA')}>Cuota</button>
            <button className={`switch-toggle-btn ${comparisonMode === 'FORECAST' ? 'active' : ''}`} onClick={() => setComparisonMode('FORECAST')}>Forecast</button>
          </div>
          {/* ✅ NUEVO BOTÓN DE DEVOLUCIONES (4 ESTADOS) */}
          <div className="switch-toggle-group">
            <button className={`switch-toggle-btn ${devMode === 'NONE' ? 'active' : ''}`} onClick={() => setDevMode('NONE')}>Sin Dev.</button>
            <button className={`switch-toggle-btn ${devMode === 'DEV' ? 'active' : ''}`} onClick={() => setDevMode('DEV')}>Con Dev.</button>
            <button className={`switch-toggle-btn ${devMode === 'FIN' ? 'active' : ''}`} onClick={() => setDevMode('FIN')}>Con D.FIN</button>
            <button className={`switch-toggle-btn ${devMode === 'NETO' ? 'active' : ''}`} onClick={() => setDevMode('NETO')}>NETO</button>
          </div>
          <div className="date-filters-group">
            {/* ✅ NUEVO FILTRO DE DÍA */}
            <MultiSelectFilter 
              label="Día" 
              name="dias" 
              icon={Calendar} 
              options={Array.from({length: 31}, (_, i) => (i + 1).toString())} 
              selectedValues={selectedDay} 
              onChange={(name, vals) => setSelectedDay(vals)} 
            />
            <MultiSelectFilter 
              label="Mes" 
              name="meses" 
              icon={Calendar} 
              options={nombresMeses} 
              selectedValues={selectedMeses} 
              onChange={(name, vals) => setSelectedMeses(vals)} 
            />
            <MultiSelectFilter 
              label="Año" 
              name="anios" 
              icon={Calendar} 
              options={availableYears.map(year => ({value: year, label: year.toString()}))} 
              selectedValues={selectedAnios} 
              onChange={(name, vals) => {
                console.log('📅 Años seleccionados:', vals);
                setSelectedAnios(vals);
              }} 
            />
          </div>
          {/* GlobalSearch movido fuera de date-filters-group para permitir el wrap en móvil */}
          <div style={isMobile ? { width: '100%', marginTop: '8px' } : {}}>
            <GlobalSearch 
              availableOptions={availableOptions}
              filterLabels={filterLabels}
              filters={filters}
              setFilters={setFilters}
            />
          </div>
        </div>
      </header>

      {(() => {
        const timeVsSalesDiff = parseFloat(metrics.porcentaje) - parseFloat(workDayMetrics.percentage);
        return (
          <div className="working-days-bar">
            <div className="wd-item"><span>Días Hábiles:</span> <strong>{workDayMetrics.total}</strong></div>
            <div className="wd-item"><span>Transcurridos:</span> <strong>{workDayMetrics.elapsed}</strong></div>
            <div className="wd-item"><span>Avance:</span> <strong>{workDayMetrics.percentage}%</strong></div>
            <div className="wd-separator"></div>
            <div className={`wd-item variation-item ${timeVsSalesDiff >= 0 ? 'positive' : 'negative'}`}>
              {timeVsSalesDiff >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              <strong>{timeVsSalesDiff.toFixed(1)}%</strong>
              <span className="wd-label">(vs Avance)</span>
            </div>
          </div>
        );
      })()}

      <div className={`filters-sidebar-container ${showMobileFilters ? 'open' : ''}`}>
        <div className="filters-sidebar-header">
          <h3>Filtros Disponibles</h3>
          <div className="filters-header-actions">
            <button className="reset-filters-btn" onClick={handleResetFilters} title="Restablecer filtros">
              <FilterX size={16} />
            </button>
            <button className="close-filters-btn" onClick={() => setShowMobileFilters(false)}>
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="filters-grid" style={{ gap: '8px' }}>
          {/* ← DEBUG TEMPORAL: */}
          
          {Object.keys(filters).map((key) => (
            <MultiSelectFilter 
            key={key} 
            label={filterLabels[key] || key} 
            name={key} 
            icon={key.includes('CLI') ? Users : Tag} 
            options={availableOptions[key] || []}
            selectedValues={filters[key] || []}
            onChange={(name, vals) => {
              console.log(`🔄 Cambio filtro ${name}:`, vals.slice(0, 3));
              setFilters(prev => {
                const newFilters = { ...prev, [name]: vals };
                console.log(`✅ setFilters ATÓMICO ${name}:`, newFilters[name].length);
                return newFilters;
              });
            }} 
          />
          ))}
          {/* Nuevo filtro de bajo cumplimiento - Integrado visualmente */}
          <div className="filter-pill-container"> {/* Contenedor para mantener el ancho */}
            <button className={`filter-pill-button ${lowCompliance ? 'active low-compliance' : ''}`} onClick={() => setLowCompliance(!lowCompliance)} title="Filtrar por cumplimiento menor al 80%">
              <span className="pill-label">Cumplimiento &lt; 80%</span>
            </button>
          </div>
          <button className="filter-pill-button reset-filters" onClick={handleResetFilters} title="Restablecer filtros">
            <FilterX size={12} className="pill-icon" />
            <span className="pill-label">Limpiar</span>
          </button>
        </div>
      </div>
      {showMobileFilters && <div className="filters-overlay" onClick={() => setShowMobileFilters(false)} />}

      <div className="dashboard-indicators">
        <div className="indicator-card v-real">
          <div className="card-content">
            <span className="indicator-label">USD Reales</span>
            <div className="indicator-row">
              <span className="indicator-value">{formatDollar(metrics.v_cantidad)}</span>
              <div className="card-variations" style={{ opacity: querying ? 0.6 : 1, transition: 'opacity 0.3s' }}>
                  {!isMobile && (
                  <span className={`variation-item ${metrics.var_v_anual >= 0 ? 'positive' : 'negative'}`}>
                    {metrics.var_v_anual >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
                    {metrics.var_v_anual}% Anual
                  </span>
                  )}
                  <span className={`variation-item ${metrics.var_v_mensual >= 0 ? 'positive' : 'negative'}`}>
                    {metrics.var_v_mensual >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
                    {metrics.var_v_mensual}% Mensual
                  </span>
              </div>
            </div>
          </div>
        </div>
        <div className="indicator-card q-meta">
          <div className="card-content">
            <span className="indicator-label">{comparisonMode === 'FORECAST' ? 'Meta Forecast' : 'Meta Cuota'} (USD)</span>
            <div className="indicator-row">
              <span className="indicator-value">{formatDollar(metrics.q_cuota)}</span>
              <div className="card-variations" style={{ opacity: querying ? 0.6 : 1, transition: 'opacity 0.3s' }}>
                  {!isMobile && (
                  <span className={`variation-item ${metrics.var_q_anual >= 0 ? 'positive' : 'negative'}`}>
                     {metrics.var_q_anual >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
                     {metrics.var_q_anual}% Anual
                   </span>
                   )}
                   <span className={`variation-item ${metrics.var_q_mensual >= 0 ? 'positive' : 'negative'}`}>
                     {metrics.var_q_mensual >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
                     {metrics.var_q_mensual}% Mensual
                   </span>
              </div>
            </div>
          </div>
        </div>
        <div className="indicator-card p-cumplimiento">
          <div className="card-content">
            <span className="indicator-label">% Cumplimiento</span>
            <div className="indicator-row">
              <span className="indicator-value">{`${metrics.porcentaje}%`}</span> {/* ✅ SIN $ aquí */}
              <div className="card-variations" style={{ opacity: querying ? 0.6 : 1, transition: 'opacity 0.3s' }}>
                  {!isMobile && (
                  <span className={`variation-item ${metrics.var_p_anual >= 0 ? 'positive' : 'negative'}`}>
                    {metrics.var_p_anual >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
                    {metrics.var_p_anual}% Anual
                  </span>
                  )}
                  <span className={`variation-item ${metrics.var_p_mensual >= 0 ? 'positive' : 'negative'}`}>
                    {metrics.var_p_mensual >= 0 ? <TrendingUp size={14}/> : <TrendingDown size={14}/>}
                    {metrics.var_p_mensual}% Mensual
                  </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="charts-grid main-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s' }}>
        <div className="chart-container">
          <h3 className="chart-title">Evolución Mensual{filterTitle}</h3>
          <ResponsiveContainer width="100%" height={185}>
            <ComposedChart data={chartData} margin={{ top: 20, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff20" animationDuration={isMobile ? 0 : 400} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 8, fill: '#fff' }} />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tickFormatter={formatK} tick={{ fontSize: 9, fill: '#fff' }} />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={false} width={0} domain={[0, (max) => Math.max(max, 100) * 1.2]} />
              <Tooltip content={<CustomChartTooltip />} />
              <Legend verticalAlign="bottom" height={16} iconType="circle" wrapperStyle={{ fontSize: '10px', backgroundColor: 'rgba(255, 255, 255, 0.85)', borderRadius: '12px', padding: '5px', width: '90%', margin: '0 auto', left: '5%', bottom: 0, display: 'flex', justifyContent: 'center', color: '#fff' }} />
              <Bar yAxisId="left" dataKey="Cuota" fill="#3c4b68" radius={[2, 2, 0, 0]} barSize={12} animationDuration={isMobile ? 0 : 1500} />
              <Bar yAxisId="left" dataKey="Venta" fill="#0068b3" radius={[2, 2, 0, 0]} barSize={12} animationDuration={isMobile ? 500 : 1500}>
                <LabelList dataKey="Venta" position="top" formatter={formatK} style={{ fontSize: '9px', fontWeight: 'bold', fill: '#fff' }} />
              </Bar>
              <Line yAxisId="right" type="monotone" dataKey="Logro" stroke="#00ba52" strokeWidth={2} dot={{ r: 3 }}>
                <LabelList dataKey="Logro" position="top" formatter={(v) => `${v}%`} style={{ fontSize: '9px', fill: '#00ba52', fontWeight: 'bold' }} />
              </Line>
            </ComposedChart>
          </ResponsiveContainer>
          </div>

          <div className="chart-container">
                    <h3 className="chart-title">Participación Marcas (%){filterTitle}</h3>
                    <ResponsiveContainer width="100%" height={185}>
                      <PieChart>
                        <Pie data={pieData} cx="50%" cy="45%" innerRadius={40} outerRadius={65} paddingAngle={4} dataKey="value" labelLine={false} label={renderCustomizedLabel} animationDuration={isMobile ? 500 : 1500} animationEasing="ease-out">
                          {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="#fff" strokeWidth={0} />)}
                        </Pie>
                        <Tooltip formatter={(value) => formatDollar(value)} /><Legend verticalAlign="bottom" height={16} iconType="circle" wrapperStyle={{ fontSize: '7px', backgroundColor: 'rgba(255, 255, 255, 0.85)', borderRadius: '12px', padding: '5px', width: '90%', margin: '0 auto', left: '5%', bottom: 0, display: 'flex', justifyContent: 'center', color: '#fff' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
              </div>
       <div className="charts-grid bottom-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s' }}>
              <div className="chart-container mini">
                <h3 className="chart-title">Participación Región{filterTitle}</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={regionData} layout="vertical" margin={{ top: 5, right: 50, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={false} stroke="#ffffff10" />
                    <XAxis type="number" hide />
                    <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#fff' }} width={80} />
                    <Tooltip cursor={{fill: 'transparent'}} formatter={(value) => formatDollar(value)} labelStyle={{color: '#333'}} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={20} animationDuration={1000}>
                      {regionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                      <LabelList dataKey="value" position="right" formatter={(v) => formatK(v)} style={{ fill: '#cbd5e1', fontSize: 10, fontWeight: 'bold' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
      
              <div className="chart-container mini table-fixed-height" style={{ flex: 2 }}>
                <div className="table-header-container">
                  <h3 className="chart-title">Desempeño por Cliente Principal (Bultos)</h3>
                  <button className="export-table-btn" onClick={() => exportToExcel(tableData, 'Clientes_Bultos', 'generic')}>
                    <Download size={12} />
                  </button>
                </div>
                <div className="minimal-table-wrapper">
                  <div className="table-header">
                    <div className="col-name">Cliente</div>
                    <div className="col-data">{comparisonMode === 'FORECAST' ? 'FC' : 'Cuota'}</div>
                    <div className="col-data">Venta</div>
                    <div className="col-data">% Efec.</div>
                    <div className="col-status">Est.</div>
                    <div className="col-data mobile-hidden">% Part.</div>
                  </div>
                  
                  <div className="table-body">
                    {tableData && tableData.map((item, idx) => {
                      const efecVal = Number(item.efectividad || 0);
                      const partVal = metrics.v_cantidad > 0 ? (item.venta / metrics.v_cantidad) * 100 : 0;
                      return (
                        <div className="table-row" key={idx}>
                          <div className="col-name truncate" title={item.CLI_PRIN}>{item.CLI_PRIN || '---'}</div>
                          <div className="col-data">{formatDollar(item.cuota)}</div>
                          <div className="col-data">{formatDollar(item.venta)}</div>
                          <div className="col-data bold">{efecVal.toFixed(1)}%</div>
                          <div className="col-status">
                            <div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecVal) }}></div>
                          </div>
                          <div className="col-data mobile-hidden">{partVal.toFixed(1)}%</div>
                        </div>
                      );
                    })}
                  </div>
      
                  <div className="table-footer">
                    <div className="col-name">TOTALES</div>
                    {(() => {
                      // Usamos las métricas globales para asegurar coincidencia exacta con las tarjetas
                      const tC = metrics.q_cuota;
                      const tV = metrics.v_cantidad;
                      const tE = tC > 0 ? (tV / tC) * 100 : 0;
                      const tP = 100; // El total siempre es el 100%
                      return (
                        <>
                          <div className="col-data">{formatDollar(tC)}</div>
                          <div className="col-data">{formatDollar(tV)}</div>
                          <div className="col-data bold">{tE.toFixed(1)}%</div>
                          <div className="col-status">
                            <div className="status-dot" style={{ backgroundColor: getSemaforoColor(tE) }}></div>
                          </div>
                          <div className="col-data mobile-hidden">{tP.toFixed(1)}%</div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
      
            {/* NUEVAS TABLAS: ASESORES Y MARCAS (LADO A LADO) */}
            <div className="charts-grid bottom-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s', marginTop: '20px' }}>
              {/* TABLA DE ASESORES */}
              <div className="chart-container mini table-fixed-height">
                <div className="table-header-container">
                  <h3 className="chart-title">Desempeño por Asesor</h3>
                  <button className="export-table-btn" onClick={() => exportToExcel(asesoresTableData, 'Asesores_USD', 'generic')}>
                    <Download size={12} />
                  </button>
                </div>
                <div className="minimal-table-wrapper">
                  <div className="table-header">
                    <div className="col-name">Asesor</div>
                    <div className="col-data">{comparisonMode === 'FORECAST' ? 'FC' : 'Cuota'}</div>
                    <div className="col-data">Venta</div>
                    <div className="col-data">% Efec.</div>
                    <div className="col-status">Est.</div>
                    <div className="col-data mobile-hidden">% Part.</div>
                  </div>
                  
                  <div className="table-body">
                    {asesoresTableData && asesoresTableData.map((item, idx) => {
                      const efecVal = Number(item.efectividad || 0);
                      const partVal = metrics.v_cantidad > 0 ? (item.venta / metrics.v_cantidad) * 100 : 0;
                      return (
                        <div className="table-row" key={idx}>
                          <div className="col-name truncate" title={item.CLI_VEN_DES}>{item.CLI_VEN_DES || '---'}</div>
                          <div className="col-data">{formatDollar(item.cuota)}</div>
                          <div className="col-data">{formatDollar(item.venta)}</div>
                          <div className="col-data bold">{efecVal.toFixed(1)}%</div>
                          <div className="col-status">
                            <div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecVal) }}></div>
                          </div>
                          <div className="col-data mobile-hidden">{partVal.toFixed(1)}%</div>
                        </div>
                      );
                    })}
                  </div>
      
                  <div className="table-footer">
                    <div className="col-name">TOTALES</div>
                    {(() => {
                      const tC = metrics.q_cuota;
                      const tV = metrics.v_cantidad;
                      const tE = tC > 0 ? (tV / tC) * 100 : 0;
                      const tP = 100;
                      return (
                        <>
                          <div className="col-data">{formatDollar(tC)}</div>
                          <div className="col-data">{formatDollar(tV)}</div>
                          <div className="col-data bold">{tE.toFixed(1)}%</div>
                          <div className="col-status">
                            <div className="status-dot" style={{ backgroundColor: getSemaforoColor(tE) }}></div>
                          </div>
                          <div className="col-data mobile-hidden">{tP.toFixed(1)}%</div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
      
              {/* TABLA DE MARCAS */}
              <div className="chart-container mini table-fixed-height">
                <div className="table-header-container">
                  <h3 className="chart-title">Desempeño por Marca</h3>
                  <button className="export-table-btn" onClick={() => exportToExcel(marcasTableData, 'Marcas_USD', 'hierarchical')}>
                    <Download size={12} />
                  </button>
                </div>
                <div className="minimal-table-wrapper">
                  <div className="table-header">
                    <div className="col-name">Marca</div>
                    <div className="col-data">{comparisonMode === 'FORECAST' ? 'FC' : 'Cuota'}</div>
                    <div className="col-data">Venta</div>
                    <div className="col-data">% Efec.</div>
                    <div className="col-status">Est.</div>
                    <div className="col-data mobile-hidden">% Part.</div>
                  </div>
                  
                  <div className="table-body">
                    {marcasTableData && marcasTableData.map((item, idx) => {
                      const efecVal = Number(item.efectividad || 0);
                      const partVal = metrics.v_cantidad > 0 ? (item.venta / metrics.v_cantidad) * 100 : 0;
                      const isExpanded = expandedMarcas[item.BI_MARCA];
                      return (
                        <React.Fragment key={idx}>
                        <div className="table-row advisor-row" onClick={() => toggleMarca(item.BI_MARCA)}>
                          <div className="col-name truncate" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                             {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                             <span title={item.BI_MARCA}>{item.BI_MARCA || '---'}</span>
                          </div>
                          <div className="col-data">{formatDollar(item.cuota)}</div>
                          <div className="col-data">{formatDollar(item.venta)}</div>
                          <div className="col-data bold">{efecVal.toFixed(1)}%</div>
                          <div className="col-status">
                            <div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecVal) }}></div>
                          </div>
                          <div className="col-data mobile-hidden">{partVal.toFixed(1)}%</div>
                        </div>
                        {isExpanded && item.skus && item.skus.map((sku, sIdx) => {
                            const sEfec = sku.efectividad || 0;
                            return (
                              <div className="table-row advisor-row" key={`${idx}-${sIdx}`} style={{ backgroundColor: 'rgba(0,0,0,0.2)', fontSize: '0.7rem' }}>
                                  <div className="col-name truncate" style={{ paddingLeft: '25px' }} title={sku.BI_DESC1}>{sku.BI_DESC1}</div>
                                  <div className="col-data">{formatDollar(sku.cuota)}</div>
                                  <div className="col-data">{formatDollar(sku.venta)}</div>
                                  <div className="col-data">{sEfec.toFixed(1)}%</div>
                                  <div className="col-status">
                                      <div className="status-dot" style={{ width: '6px', height: '6px', backgroundColor: getSemaforoColor(sEfec) }}></div>
                                  </div>
                                  <div className="col-data mobile-hidden">-</div>
                              </div>
                            );
                        })}
                        </React.Fragment>
                      );
                    })}
                  </div>
      
                  <div className="table-footer">
                    <div className="col-name">TOTALES</div>
                    {(() => {
                      const tC = metrics.q_cuota;
                      const tV = metrics.v_cantidad;
                      const tE = tC > 0 ? (tV / tC) * 100 : 0;
                      const tP = 100;
                      return (
                        <>
                          <div className="col-data">{formatDollar(tC)}</div>
                          <div className="col-data">{formatDollar(tV)}</div>
                          <div className="col-data bold">{tE.toFixed(1)}%</div>
                          <div className="col-status">
                            <div className="status-dot" style={{ backgroundColor: getSemaforoColor(tE) }}></div>
                          </div>
                          <div className="col-data mobile-hidden">{tP.toFixed(1)}%</div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
      
              {/* TABLA DE PROVEEDORES */}
                    <div className="charts-grid bottom-row" style={{ opacity: querying ? 0.8 : 1, transition: 'opacity 0.3s', marginTop: '20px' }}>
                      <div className="chart-container mini table-fixed-height" style={{ flex: 1, gridColumn: 'span 2' }}>
                        <div className="table-header-container">
                          <h3 className="chart-title">Desempeño por Casa</h3>
                          <button className="export-table-btn" onClick={() => exportToExcel(proveedoresTableData, 'Proveedores_Bultos', 'generic')}>
                            <Download size={12} />
                          </button>
                        </div>
                        <div className="minimal-table-wrapper">
                          <div className="table-header">
                            <div className="col-name">Casas</div>
                            <div className="col-data">{comparisonMode === 'FORECAST' ? 'FC' : 'Cuota'}</div>
                            <div className="col-data">Venta</div>
                            <div className="col-data">% Efec.</div>
                            <div className="col-status">Est.</div>
                            <div className="col-data mobile-hidden">% Part.</div>
                          </div>
                          
                          <div className="table-body">
                            {proveedoresTableData && proveedoresTableData.map((item, idx) => {
                              const efecVal = Number(item.efectividad || 0);
                              const partVal = metrics.v_cantidad > 0 ? (item.venta / metrics.v_cantidad) * 100 : 0;
                              return (
                                <div className="table-row" key={idx}>
                                  <div className="col-name truncate" title={item.BI_PROVEEDOR}>{item.BI_PROVEEDOR || '---'}</div>
                                  <div className="col-data">{formatDollar(item.cuota)}</div>
                                  <div className="col-data">{formatDollar(item.venta)}</div>
                                  <div className="col-data bold">{efecVal.toFixed(1)}%</div>
                                  <div className="col-status">
                                    <div className="status-dot" style={{ backgroundColor: getSemaforoColor(efecVal) }}></div>
                                  </div>
                                  <div className="col-data mobile-hidden">{partVal.toFixed(1)}%</div>
                                </div>
                              );
                            })}
                          </div>
              
                          <div className="table-footer">
                            <div className="col-name">TOTALES</div>
                            {(() => {
                              const tC = tableTotals.cuota || metrics.q_cuota;      // ✅ Usa totales reales
                              const tV = tableTotals.venta || metrics.v_cantidad;   // ✅ Usa totales reales
                              const tE = tC > 0 ? (tV / tC) * 100 : 0;             // ✅ Calcula con reales
                              const tP = 100;
                              return (
                                <>
                                  <div className="col-data">{formatDollar(tC)}</div>
                                  <div className="col-data">{formatDollar(tV)}</div>
                                  <div className="col-data bold">{tE.toFixed(1)}%</div>
                                  <div className="col-status">
                                    <div className="status-dot" style={{ backgroundColor: getSemaforoColor(tE) }}></div>
                                  </div>
                                  <div className="col-data mobile-hidden">{tP.toFixed(1)}%</div>
                                </>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
          </div>
                     
        
        
      
      <footer className="status-footer">
        {querying ? "SINCRONIZANDO..." : `VISTA ACTUAL: ${selectedMeses.length > 0 ? selectedMeses.join(', ') : 'TODOS'} ${selectedAnios.length > 0 ? selectedAnios.join(', ') : ''}`}
      </footer>

      </motion.div>
    </motion.div>
  );
};

export default DashboardUSDSales;